import { Suspense, lazy } from "react";
import "./App.css";
import Menu from "./components/Menu/menu.js";
import Footer from "./pages/Footer/footer.js";
import Seo from "./components/Seo/seo.js";
import { localizedPath, splitRoute, useRouter } from "./router.js";
import { useLocale } from "./locale-context.js";

const Home = lazy(() => import("./pages/Home/home.js"));
const About = lazy(() => import("./pages/About/about.js"));
const Skills = lazy(() => import("./pages/Skills/skills.js"));
const Contacts = lazy(() => import("./pages/Contacts/contacts.js"));
const Portfolio = lazy(() => import("./pages/Portfolio/portfolio.js"));
const Blog = lazy(() => import("./pages/Blog/blog.js"));
const JournalPost = lazy(() => import("./pages/Blog/journalPost.js"));
const ProjectCaseStudy = lazy(
  () => import("./pages/ProjectCaseStudy/projectCaseStudy.js"),
);
const NotFound = lazy(() => import("./pages/NotFound/notFound.js"));

const metadata = {
  "/": [
    "Ricardo Beaujon | Full-Stack Developer in Estonia",
    "Ricardo Beaujon is a Full-Stack Developer in Estonia building responsive interfaces, backend APIs, databases, integrations and complete digital products.",
  ],
  "/about/": [
    "About Ricardo Beaujon | Full-Stack Developer",
    "Learn about Ricardo Beaujon, a Full-Stack Developer based in Estonia with decades of practical problem solving and experience building complete digital products.",
  ],
  "/skills/": [
    "Full-Stack Development Skills | Ricardo Beaujon",
    "Explore Ricardo Beaujon's frontend, backend, API, database, infrastructure and delivery skills, including React, TypeScript, Node.js and SQL.",
  ],
  "/projects/": [
    "Software Projects by Ricardo Beaujon",
    "Explore software projects developed by Ricardo Beaujon, including IAMNovaCore, Enviamed and web applications spanning frontend, backend and data systems.",
  ],
  "/journal/": [
    "Development Journal | Ricardo Beaujon",
    "Technical notes by Ricardo Beaujon about Pine Script indicators, realtime alerts, market analysis tools and software development.",
  ],
  "/contact/": [
    "Contact Ricardo Beaujon",
    "Contact Ricardo Beaujon, a Full-Stack Developer based in Estonia, to discuss software products, web applications, APIs and integrations.",
  ],
  "/projects/iamnovacore/": [
    "IAMNovaCore | Identity and Access Management Project",
    "A developer case study by Ricardo Beaujon about IAMNovaCore, an identity and access management platform for connected business operations.",
  ],
  "/projects/enviamed/": [
    "Enviamed | Credit and Collections ERP Project",
    "A developer case study by Ricardo Beaujon about Enviamed, a full-stack credit and collections system for invoices, payments, reconciliation and analytics.",
  ],
};
const localizedMetadata = {
  ES: {
    "/": [
      "Ricardo Beaujon | Desarrollador Full-Stack en Estonia",
      "Ricardo Beaujon es un desarrollador Full-Stack en Estonia que crea interfaces responsive, APIs backend, bases de datos, integraciones y productos digitales completos.",
    ],
    "/about/": [
      "Sobre Ricardo Beaujon | Desarrollador Full-Stack",
      "Conoce a Ricardo Beaujon, desarrollador Full-Stack en Estonia con décadas de experiencia práctica resolviendo problemas y creando productos digitales completos.",
    ],
    "/skills/": [
      "Habilidades de desarrollo Full-Stack | Ricardo Beaujon",
      "Explora las habilidades frontend, backend, APIs, bases de datos, infraestructura y entrega de Ricardo Beaujon, incluidas React, TypeScript, Node.js y SQL.",
    ],
    "/projects/": [
      "Proyectos de software de Ricardo Beaujon",
      "Explora proyectos desarrollados por Ricardo Beaujon, como IAMNovaCore, Enviamed y aplicaciones que abarcan frontend, backend y sistemas de datos.",
    ],
    "/journal/": [
      "Diario de desarrollo | Ricardo Beaujon",
      "Notas técnicas de Ricardo Beaujon sobre indicadores Pine Script, alertas en tiempo real, herramientas de análisis de mercado y desarrollo de software.",
    ],
    "/contact/": [
      "Contactar a Ricardo Beaujon",
      "Contacta a Ricardo Beaujon, desarrollador Full-Stack en Estonia, para conversar sobre productos de software, aplicaciones web, APIs e integraciones.",
    ],
    "/projects/iamnovacore/": [
      "IAMNovaCore | Proyecto de identidad y acceso",
      "Caso de estudio de Ricardo Beaujon sobre IAMNovaCore, una plataforma de gestión de identidad y acceso para operaciones empresariales conectadas.",
    ],
    "/projects/enviamed/": [
      "Enviamed | Proyecto ERP de crédito y cobranzas",
      "Caso de estudio de Ricardo Beaujon sobre Enviamed, un sistema full-stack de crédito y cobranzas para facturas, pagos, conciliación y analítica.",
    ],
  },
  ET: {
    "/": [
      "Ricardo Beaujon | Full-Stack arendaja Eestis",
      "Ricardo Beaujon on Eestis tegutsev Full-Stack arendaja, kes loob kohanduvaid liideseid, backend API-sid, andmebaase, integratsioone ja terviklikke digitooteid.",
    ],
    "/about/": [
      "Ricardo Beaujonist | Full-Stack arendaja",
      "Tutvu Eestis tegutseva Full-Stack arendaja Ricardo Beaujoniga, kellel on aastakümnete jagu praktilist probleemilahenduse ja terviklike digitoodete loomise kogemust.",
    ],
    "/skills/": [
      "Full-Stack arendusoskused | Ricardo Beaujon",
      "Tutvu Ricardo Beaujoni frontendi, backendi, API-de, andmebaaside, infrastruktuuri ja tarne oskustega, sealhulgas React, TypeScript, Node.js ning SQL.",
    ],
    "/projects/": [
      "Ricardo Beaujoni tarkvaraprojektid",
      "Tutvu Ricardo Beaujoni projektidega, sealhulgas IAMNovaCore, Enviamed ning frontendi, backendi ja andmesüsteeme hõlmavad rakendused.",
    ],
    "/journal/": [
      "Arenduspäevik | Ricardo Beaujon",
      "Ricardo Beaujoni tehnilised märkmed Pine Scripti indikaatorite, reaalaja hoiatuste, turuanalüüsi tööriistade ja tarkvaraarenduse kohta.",
    ],
    "/contact/": [
      "Võta ühendust Ricardo Beaujoniga",
      "Võta ühendust Eestis tegutseva Full-Stack arendaja Ricardo Beaujoniga, et arutada tarkvaratooteid, veebirakendusi, API-sid ja integratsioone.",
    ],
    "/projects/iamnovacore/": [
      "IAMNovaCore | Identiteedi- ja ligipääsuprojekt",
      "Ricardo Beaujoni juhtumiuuring IAMNovaCore'ist, ühendatud äritegevuse identiteedi- ja ligipääsuhalduse platvormist.",
    ],
    "/projects/enviamed/": [
      "Enviamed | Krediidi- ja inkasso ERP projekt",
      "Ricardo Beaujoni juhtumiuuring Enviamedist, arvete, maksete, kooskõlastamise ja analüütika full-stack krediidi- ning inkassosüsteemist.",
    ],
  },
};

function App() {
  const { pathname } = useRouter();
  const { locale, t } = useLocale();
  const { route } = splitRoute(pathname);
  const page = route === "/" ? "home" : route.split("/").filter(Boolean)[0];
  const meta = (locale === "EN"
    ? metadata[route]
    : localizedMetadata[locale]?.[route]) || [
      locale === "ES"
        ? "Página no encontrada | Ricardo Beaujon"
        : locale === "ET"
          ? "Lehte ei leitud | Ricardo Beaujon"
          : "Page not found | Ricardo Beaujon",
      locale === "ES"
        ? "No se encontró la página solicitada."
        : locale === "ET"
          ? "Soovitud lehte ei leitud."
          : "The requested page could not be found on Ricardo Beaujon's portfolio.",
    ];
  const robots = metadata[route]
    ? "index,follow,max-image-preview:large"
    : "noindex,nofollow";
  const path = localizedPath(locale, route);
  const pageUrl = `https://rbeaujon.com${path}`;
  let schema;
  if (route === "/about/")
    schema = {
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      "@id": `${pageUrl}#profile`,
      url: pageUrl,
      name: meta[0],
      mainEntity: { "@id": "https://rbeaujon.com/#person" },
    };
  if (route === "/projects/iamnovacore/")
    schema = {
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      name: "IAMNovaCore",
      description: meta[1],
      url: pageUrl,
      creator: { "@id": "https://rbeaujon.com/#person" },
      sameAs: "https://iamnovacore.com/",
    };
  if (route === "/projects/enviamed/")
    schema = {
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      name: "Enviamed",
      description: meta[1],
      url: pageUrl,
      creator: { "@id": "https://rbeaujon.com/#person" },
      sameAs: "https://enviamed.rbeaujon.com/",
    };
  let content;
  if (route === "/") content = <Home />;
  else if (route === "/about/") content = <About />;
  else if (route === "/skills/") content = <Skills />;
  else if (route === "/projects/") content = <Portfolio />;
  else if (route === "/journal/") content = <Blog />;
  else if (route === "/contact/") content = <Contacts />;
  else if (route === "/projects/iamnovacore/")
    content = <ProjectCaseStudy project="iamnovacore" />;
  else if (route === "/projects/enviamed/")
    content = <ProjectCaseStudy project="enviamed" />;
  else if (/^\/journal\/[^/]+\/$/.test(route))
    content = <JournalPost slug={route.split("/")[2]} />;
  else content = <NotFound />;
  return (
    <div className="body">
      <Seo
        title={meta[0]}
        description={meta[1]}
        path={path}
        locale={locale}
        robots={robots}
        schema={schema}
        preloadImage={
          route === "/" ? "/project-media/iamnovacore-poster.jpg" : undefined
        }
      />
      <Menu activePage={page} />
      <Suspense
        fallback={
          <main className="route-loading" aria-live="polite">
            {t.loading}
          </main>
        }
      >
        {content}
      </Suspense>
      <Footer activePage={page} />
    </div>
  );
}
export default App;
